import React, {useState, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Table,
  Modal,
  Form,
  Badge,
  Collapse,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaGift,
  FaCheckCircle,
  FaTags,
  FaUsers,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaExclamationCircle,
} from "react-icons/fa";
import allCampaigns from "../data/campaigns.json";
import allMetricsData from "../data/campaignMetrics.json";
import allLists from "../data/broadcastLists.json";
import * as XLSX from "xlsx";

// Componente reutilizable para las tarjetas de KPIs
const KpiCard = ({icon, title, value, color}) => (
  <Card className={`shadow-sm rounded-4 border-0 h-100`}>
    <Card.Body className="d-flex align-items-center p-3">
      <div className={`fs-3 text-${color} me-3`}>{icon}</div>
      <div>
        <div className="fs-5 fw-bold">{value}</div>
        <div className="text-muted text-uppercase small">{title}</div>
      </div>
    </Card.Body>
  </Card>
);

const Incentives = ({incentives, setIncentives, contacts}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingIncentive, setEditingIncentive] = useState(null);
  const [selectedIncentiveId, setSelectedIncentiveId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [incentiveToDelete, setIncentiveToDelete] = useState(null);
  // Estado para los campos del formulario del modal
  const [formState, setFormState] = useState({
    name: "",
    description: "",
    codePrefix: "",
    isActive: true,
  });

  // Limpiar el formulario cuando el modal se cierra o abre para crear
  const resetForm = () =>
    setFormState({name: "", description: "", codePrefix: "", isActive: true});

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIncentive(null);
  };

  const handleShowCreateModal = () => {
    setEditingIncentive(null);
    resetForm();
    setShowModal(true);
  };

  const handleShowEditModal = (incentive) => {
    setEditingIncentive(incentive);
    setFormState(incentive); // Carga los datos del incentivo en el formulario
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const {name, value, type, checked} = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" || type === "switch" ? checked : value,
    }));
  };

  const handleSaveIncentive = (e) => {
    e.preventDefault();
    if (editingIncentive) {
      // Lógica de actualización
      setIncentives(
        incentives.map((inc) =>
          inc.id === editingIncentive.id ? {...inc, ...formState} : inc
        )
      );
    } else {
      // Lógica de creación
      const newIncentive = {
        id: `inc-${Date.now()}`, // ID único simple
        ...formState,
        createdAt: new Date().toISOString(),
      };
      setIncentives([...incentives, newIncentive]);
    }
    handleCloseModal();
  };

  const handleDeleteClick = (incentive) => {
    setIncentiveToDelete(incentive);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (incentiveToDelete) {
      setIncentives(
        incentives.filter((inc) => inc.id !== incentiveToDelete.id)
      );
    }
    setShowDeleteModal(false);
    setIncentiveToDelete(null);
  };

  const handleDeactivateIncentive = (incentiveToDeactivate) => {
    setIncentives(
      incentives.map((inc) =>
        inc.id === incentiveToDeactivate.id ? {...inc, isActive: false} : inc
      )
    );
  };

  // --- Cálculo de Métricas ---
  const incentiveMetrics = useMemo(() => {
    let totalRequests = 0;
    const allMetrics = allMetricsData;
    const requestsPerIncentive = {};
    const usersPerIncentive = {};

    // Inicializamos los contadores y listas
    incentives.forEach((inc) => {
      requestsPerIncentive[inc.id] = 0;
      usersPerIncentive[inc.id] = [];
    });

    // Recorremos las campañas que tienen un incentivo asociado
    allCampaigns.forEach((campaign) => {
      if (campaign.incentiveId) {
        const metrics = allMetrics.find((m) => m.campaignId === campaign.id);
        if (metrics) {
          const campaignRecipients = new Set(
            campaign.sentToListIds.flatMap(
              (listId) => allLists.find((l) => l.id === listId)?.members || []
            )
          );

          // Simulamos qué usuarios usaron el incentivo
          const incentiveUsers = [...campaignRecipients]
            .sort(() => 0.5 - Math.random()) // Mezclamos para aleatoriedad
            .slice(0, Math.floor(metrics.read * 0.45)); // 45% de los que leyeron

          totalRequests += incentiveUsers.length;
          if (requestsPerIncentive[campaign.incentiveId] !== undefined) {
            requestsPerIncentive[campaign.incentiveId] += incentiveUsers.length;
          }

          // Añadimos los detalles de los usuarios
          incentiveUsers.forEach((userId) => {
            const contact = contacts.find((c) => c.id === userId);
            if (contact && campaign.incentiveId) {
              const incentive = incentives.find(
                (i) => i.id === campaign.incentiveId
              );
              const code = `${incentive?.codePrefix}-${contact.id}${campaign.id}`;
              usersPerIncentive[campaign.incentiveId].push({...contact, code});
            }
          });
        }
      }
    });

    const activeIncentives = incentives.filter((i) => i.isActive).length;

    return {
      totalRequests,
      requestsPerIncentive,
      usersPerIncentive,
      activeIncentives,
    };
  }, [incentives, contacts]);

  const handleToggleDetails = (incentiveId) => {
    setSelectedIncentiveId(
      selectedIncentiveId === incentiveId ? null : incentiveId
    );
  };

  const handleExportUsers = (incentive) => {
    const users = incentiveMetrics.usersPerIncentive[incentive.id] || [];
    const dataToExport = users.map((user) => ({
      Nombre: `${user.firstName} ${user.lastName}`,
      RUT: user.rut,
      Teléfono: user.phone,
      Empresa: user.transportista,
      "Código Asignado": user.code,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios");
    XLSX.writeFile(workbook, `Usuarios_${incentive.name}.xlsx`);
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Encabezado de Beneficios */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="fw-bold mb-1">
                <FaGift className="me-3 text-primary" />
                Gestión de Beneficios
              </h2>
              <p className="text-muted mb-0">
                Crea y administra beneficios para motivar a tu audiencia
              </p>
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={handleShowCreateModal}
              className="rounded-pill px-4"
            >
              <FaPlus className="me-2" />
              Nuevo Beneficio
            </Button>
          </div>
        </Col>
      </Row>

      {/* Fila de KPIs de Beneficios */}
      <Row className="mb-4">
        <Col md={4}>
          <KpiCard
            icon={<FaTags />}
            title="Beneficios Totales"
            value={incentives.length}
            color="primary"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaCheckCircle />}
            title="Beneficios Activos"
            value={`${incentiveMetrics.activeIncentives} de ${incentives.length}`}
            color="success"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaGift />}
            title="Total de Canjes"
            value={(incentiveMetrics.totalRequests || 0).toLocaleString(
              "es-CL"
            )}
            color="info"
          />
        </Col>
      </Row>

      <Card className="shadow-sm rounded-4 border-0">
        <Card.Header className="bg-white p-3 border-bottom-0">
          <h5 className="mb-0 fw-bold">
            <FaGift className="me-2 text-primary" />
            Todos los Beneficios
          </h5>
        </Card.Header>
        <Card.Body>
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Nombre del Incentivo</th>
                <th>Descripción</th>
                <th className="text-center">Solicitudes</th>
                <th className="text-center">Prefijo del Código</th>
                <th className="text-center">Estado</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {incentives.map((incentive) => {
                const isSelected = selectedIncentiveId === incentive.id;
                const userCount =
                  incentiveMetrics.requestsPerIncentive[incentive.id] || 0;
                return (
                  <React.Fragment key={incentive.id}>
                    <tr
                      onClick={() => handleToggleDetails(incentive.id)}
                      style={{cursor: "pointer"}}
                      className={isSelected ? "table-active" : ""}
                    >
                      <td className="fw-bold">
                        {isSelected ? (
                          <FaChevronUp className="me-2" />
                        ) : (
                          <FaChevronDown className="me-2 text-primary" />
                        )}
                        {incentive.name}
                      </td>
                      <td className="text-muted small">
                        {incentive.description}
                      </td>
                      <td className="text-center fw-bold align-middle fs-5">
                        {(userCount || 0).toLocaleString("es-CL")}
                      </td>
                      <td className="text-center align-middle">
                        <Badge bg="secondary">{incentive.codePrefix}</Badge>
                      </td>
                      <td className="text-center align-middle">
                        {incentive.isActive ? (
                          <Badge bg="success-subtle" text="success-emphasis">
                            Activo
                          </Badge>
                        ) : (
                          <Badge bg="danger-subtle" text="danger-emphasis">
                            Inactivo
                          </Badge>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          title="Editar"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowEditModal(incentive);
                          }}
                        >
                          <FaEdit />
                        </Button>
                        {userCount > 0 && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-2"
                            title="Exportar Usuarios"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportUsers(incentive);
                            }}
                          >
                            <FaFileExcel />
                          </Button>
                        )}
                        {userCount === 0 ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Eliminar"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(incentive);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        ) : (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            title="Desactivar Incentivo"
                            disabled={!incentive.isActive}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeactivateIncentive(incentive);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                    {/* Fila Colapsable con los detalles de usuarios */}
                    <tr>
                      <td colSpan="6" className="p-0 border-0">
                        <Collapse in={isSelected}>
                          <div className="p-3 bg-light">
                            <Card className="rounded-3">
                              <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">
                                  <FaUsers className="me-2" />
                                  Usuarios que solicitaron este incentivo
                                </h6>
                              </Card.Header>
                              <Card.Body>
                                {userCount > 0 ? (
                                  <Table striped bordered hover size="sm">
                                    <thead>
                                      <tr>
                                        <th>Nombre</th>
                                        <th>RUT</th>
                                        <th>Teléfono</th>
                                        <th>Empresa</th>
                                        <th>Código Asignado</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {incentiveMetrics.usersPerIncentive[
                                        incentive.id
                                      ].map((user) => (
                                        <tr key={user.id}>
                                          <td>
                                            {user.firstName} {user.lastName}
                                          </td>
                                          <td>{user.rut}</td>
                                          <td>{user.phone}</td>
                                          <td>{user.transportista}</td>
                                          <td>
                                            <Badge bg="info">{user.code}</Badge>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                ) : (
                                  <p className="text-muted text-center mb-0">
                                    Aún nadie ha solicitado este incentivo.
                                  </p>
                                )}
                              </Card.Body>
                            </Card>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal para Crear/Editar Incentivo */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Form onSubmit={handleSaveIncentive}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingIncentive ? "Editar Incentivo" : "Crear Nuevo Incentivo"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Incentivo</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Ej: Descuento Verano 2025"
                value={formState.name}
                onChange={handleFormChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Prefijo para Código</Form.Label>
              <Form.Control
                type="text"
                name="codePrefix"
                placeholder="Ej: CAFE24"
                value={formState.codePrefix}
                onChange={handleFormChange}
                required
              />
              <Form.Text>
                Este prefijo se usará para generar códigos únicos (ej:
                CAFE24-12345).
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={3}
                placeholder="Describe en qué consiste el premio o descuento."
                value={formState.description}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="active-switch"
              name="isActive"
              label="Incentivo Activo"
              checked={formState.isActive}
              onChange={handleFormChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingIncentive ? "Actualizar" : "Guardar Incentivo"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de Confirmación para Eliminar */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationCircle className="text-danger me-2" />
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que quieres eliminar el incentivo "
          <strong>{incentiveToDelete?.name}</strong>"? Esta acción no se puede
          deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Sí, Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Incentives;
